<template>
  <div
    v-if="slots.length > 0"
    class="grid w-max gap-0.5"
    :style="{
      '--slot': 'calc(var(--spacing) * 3.5)',
      gridTemplateColumns: `repeat(${slots.length}, var(--slot))`,
    }"
  >
    <div
      v-for="wave in waves"
      :key="wave.toIndex"
      :style="waveStyle(wave)"
      class="bg-tertiary-container rounded-extra-small self-center justify-self-center p-px"
    >
      <BaseWavyProgressIndicator
        class="stroke-tertiary pointer-events-none z-0 h-2!"
        :thickness="3"
        :wavelength="8"
      />
    </div>
    <div
      v-for="(slot, index) in slots"
      :key="slot.value"
      class="z-10 flex size-(--slot) items-center justify-center"
      :style="{ gridColumn: index + 1, gridRow: 1 }"
    >
      <PerspectiveDotNew
        v-if="slot.perspectives.length > 0"
        class="ease-fast-spatial size-full transition-transform duration-200"
        :perspectives="slot.perspectives"
        :all-perspectives="allPerspectives"
        :disable-animation="true"
      />
      <div
        v-else-if="!coveredSlotIndexes.has(index)"
        class="bg-outline-variant size-1/2 rounded-full"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import PerspectiveDotNew from '@/core/components/analytics/question-list/PerspectiveDotNew.vue'
  import BaseWavyProgressIndicator from '@/core/components/base/progress-indicator/BaseWavyProgressIndicator.vue'
  import type { Perspective } from '@/core/models/perspective'
  import { computed } from 'vue'

  const props = defineProps<{
    items: ReadonlyArray<{ value: number; label: string }>
    values: ReadonlyArray<{
      perspective: Perspective
      average: number | null
      numberOfFeedbacks: number
    }>
    allPerspectives: ReadonlyArray<Perspective>
  }>()

  const slots = computed(() =>
    props.items.map((item) => ({
      value: item.value,
      perspectives: props.values
        .filter((entry) => entry.average === item.value)
        .map((entry) => entry.perspective),
    })),
  )

  const waves = computed(() => {
    const filledIndexes = slots.value.flatMap((slot, index) =>
      slot.perspectives.length > 0 ? [index] : [],
    )

    return filledIndexes.slice(1).flatMap((toIndex, position) => {
      const fromIndex = filledIndexes[position]!
      if (toIndex - fromIndex <= 1) return []
      return [{ toIndex, fromIndex }]
    })
  })

  const coveredSlotIndexes = computed(() => {
    const covered = new Set<number>()
    for (const wave of waves.value) {
      for (let index = wave.fromIndex + 1; index < wave.toIndex; index += 1) {
        covered.add(index)
      }
    }
    return covered
  })

  /**
   * Spans the slots between two dots and stops at their edges.
   * The stroke sits about 2px inside the box, which leaves the gap.
   */
  const waveStyle = (wave: { fromIndex: number; toIndex: number }) => ({
    gridColumn: `${wave.fromIndex + 1} / ${wave.toIndex + 2}`,
    gridRow: 1,
    width: 'calc(100% - 2 * var(--slot))',
  })
</script>
